// Users (staff) API fetcher — Wave E.
//
// Mirrors the server router at /api/v1/users (admin-only listing, admin or
// self for read/update). Cache invalidation: ['users'] on every write.

import { apiClient } from './client';
import { endpoints } from './endpoints';
import type {
  CreateStaffInput,
  StaffListResponse,
  StaffSummary,
  UpdateStaffInput,
} from '../types/api';

export interface ListStaffFilters {
  page: number;
  limit?: number;
  status?: 'active' | 'deactivated' | 'all';
  role?: 'staff' | 'manager' | 'admin';
  q?: string;
}

export async function listStaff(filters: ListStaffFilters): Promise<StaffListResponse> {
  const params = new URLSearchParams();
  params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.status) params.set('status', filters.status);
  if (filters.role) params.set('role', filters.role);
  if (filters.q) params.set('q', filters.q);
  const res = await apiClient.get<StaffListResponse>(
    `${endpoints.users.list}?${params.toString()}`,
  );
  return res.data;
}

export async function getStaff(uuid: string): Promise<StaffSummary> {
  const res = await apiClient.get<{ staff: StaffSummary }>(endpoints.users.detail(uuid));
  return res.data.staff;
}

export async function createStaff(input: CreateStaffInput): Promise<StaffSummary> {
  const res = await apiClient.post<{ staff: StaffSummary }, CreateStaffInput>(
    endpoints.users.create,
    input,
  );
  return res.data.staff;
}

export async function updateStaff(uuid: string, input: UpdateStaffInput): Promise<StaffSummary> {
  const res = await apiClient.put<{ staff: StaffSummary }, UpdateStaffInput>(
    endpoints.users.update(uuid),
    input,
  );
  return res.data.staff;
}

export async function deleteStaff(uuid: string): Promise<void> {
  await apiClient.delete(endpoints.users.delete(uuid));
}

export async function triggerPasswordReset(uuid: string): Promise<void> {
  await apiClient.post(endpoints.users.passwordReset(uuid), {});
}
